import mammoth from 'mammoth'
import * as pdfjsLib from 'pdfjs-dist'

// PDF.jsのワーカー設定
if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`
}

export type FileReaderResult = {
  text: string
  fileName: string
  fileType: string
  error?: string
}

/**
 * ファイルを読み込んでテキストを抽出
 */
export async function readFileAsText(file: File): Promise<FileReaderResult> {
  const fileName = file.name
  const fileType = file.type || file.name.split('.').pop() || ''
  
  try {
    // テキストファイル
    if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
      const text = await file.text()
      return { text, fileName, fileType: 'txt' }
    }
    
    // Wordファイル（.docx）
    if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
        fileName.endsWith('.docx')) {
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.extractRawText({ arrayBuffer })
      return { text: result.value, fileName, fileType: 'docx' }
    }
    
    // 旧形式のWordファイル（.doc）
    if (fileType === 'application/msword' || fileName.endsWith('.doc')) {
      // 旧形式の.docファイルは直接読めないため、エラーメッセージを返す
      return { 
        text: '', 
        fileName, 
        fileType: 'doc',
        error: '旧形式のWordファイル（.doc）はサポートされていません。.docx形式に変換してください。'
      }
    }
    
    // PDFファイル
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      
      let fullText = ''
      const pageCount = pdf.numPages
      
      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const textContent = await page.getTextContent()
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ')
        fullText += pageText + '\n\n'
      }
      
      return { text: fullText.trim(), fileName, fileType: 'pdf' }
    }
    
    // RTFファイル
    if (fileType === 'application/rtf' || fileName.endsWith('.rtf')) {
      return { 
        text: '', 
        fileName, 
        fileType: 'rtf',
        error: 'RTFファイルはサポートされていません。テキストファイルまたはWord文書（.docx）に変換してください。'
      }
    }
    
    // Markdownファイル
    if (fileName.endsWith('.md') || fileName.endsWith('.markdown')) {
      const text = await file.text()
      return { text, fileName, fileType: 'markdown' }
    }
    
    // 不明なファイルタイプ
    return { 
      text: '', 
      fileName, 
      fileType: 'unknown',
      error: `サポートされていないファイル形式です: ${fileType || '不明'}`
    }
    
  } catch (error) {
    console.error('ファイル読み込みエラー:', error)
    return { 
      text: '', 
      fileName, 
      fileType,
      error: `ファイルの読み込みに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`
    }
  }
}

/**
 * 複数ファイルを読み込んで結合
 */
export async function readMultipleFiles(files: FileList | File[]): Promise<{
  combinedText: string
  fileInfos: FileReaderResult[]
  errors: string[]
}> {
  const fileArray = Array.from(files)
  const results = await Promise.all(fileArray.map(file => readFileAsText(file)))
  
  const combinedText = results
    .filter(r => !r.error && r.text)
    .map(r => `【${r.fileName}】\n${r.text}`)
    .join('\n\n---\n\n')
  
  const errors = results
    .filter(r => r.error)
    .map(r => `${r.fileName}: ${r.error}`)
  
  return {
    combinedText,
    fileInfos: results,
    errors
  }
}

/**
 * ファイルサイズのフォーマット
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * サポートされているファイル拡張子
 */
export const SUPPORTED_FILE_EXTENSIONS = ['.txt', '.docx', '.pdf', '.md', '.markdown']
export const SUPPORTED_MIME_TYPES = [
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'text/markdown'
]

/**
 * ファイルアップロード用のaccept属性値
 */
export const FILE_ACCEPT = '.txt,.docx,.pdf,.md,.markdown'